<template>
  <div class="stats-dashboard">
    <!-- Hero Header Banner -->
    <div class="stats-hero">
      <div class="hero-content">
        <div class="hero-icon-badge">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">Pénzügyi Statisztikák & Kimutatások</h1>
          <p class="hero-subtitle">Kredit alapú elszámolás, csoportos bevételek, oktatói és egyéni táncosi egyenlegek naprakész áttekintése</p>
        </div>
      </div>

      <button @click="downloadXLSX" class="export-hero-btn">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7,10 12,15 17,10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
        <span>XLSX / CSV Letöltés</span>
      </button>
    </div>

    <!-- Period Control Panel Bar -->
    <div class="control-panel-card">
      <div class="control-left">
        <div class="control-label">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-primary me-2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          <span>Időszak:</span>
        </div>

        <div class="filter-pills">
          <button 
            v-for="opt in dateFilterOptions" 
            :key="opt.value"
            class="filter-pill"
            :class="{ active: dateFilter === opt.value }"
            @click="setDateFilter(opt.value)"
          >
            {{ opt.label }}
          </button>
        </div>
      </div>

      <div class="control-right">
        <!-- Month & Year Selector -->
        <div v-if="dateFilter === 'month'" class="month-selector-group">
          <select v-model.number="selectedMonth" @change="loadStats" class="modern-select">
            <option v-for="(mName, mIdx) in monthNames" :key="mIdx" :value="mIdx + 1">{{ mName }}</option>
          </select>
          <select v-model.number="selectedYear" @change="loadStats" class="modern-select year-select">
            <option v-for="year in availableYears" :key="year" :value="year">{{ year }}</option>
          </select>
        </div>

        <!-- Custom Date Range -->
        <div v-if="dateFilter === 'custom'" class="date-range-group">
          <input type="date" v-model="customStartDate" @change="loadStats" class="modern-date-input" />
          <span class="range-separator">&rarr;</span>
          <input type="date" v-model="customEndDate" @change="loadStats" class="modern-date-input" />
        </div>
      </div>
    </div>

    <!-- Loading State Spinner -->
    <div v-if="loading" class="loading-container py-5">
      <div class="loading-pulse-ring"></div>
      <p class="loading-text">Pénzügyi adatok betöltése és feldolgozása...</p>
    </div>

    <!-- Main Content Dashboard -->
    <div v-else class="dashboard-body">
      <!-- Top 6 KPI Metric Cards Grid -->
      <div class="kpi-grid mb-4">
        <!-- KPI 1: Total Revenue Billed -->
        <div class="kpi-card kpi-blue">
          <div class="kpi-top">
            <span class="kpi-title">Előírt Tandíj (Összes)</span>
            <div class="kpi-icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
              </svg>
            </div>
          </div>
          <div class="kpi-value">{{ formatPrice(overallStats.combinedRevenue) }}</div>
          <div class="kpi-subtext">Számlázott tagdíjak összege</div>
        </div>

        <!-- KPI 2: Total Topups Paid -->
        <div class="kpi-card kpi-emerald">
          <div class="kpi-top">
            <span class="kpi-title">Befizetett Feltöltések</span>
            <div class="kpi-icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                <line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
          </div>
          <div class="kpi-value text-emerald">{{ formatPrice(overallStats.combinedPaid) }}</div>
          <div class="kpi-subtext">Jóváírt egyenlegfizetések</div>
        </div>

        <!-- KPI 3: Total Dues / Outstanding -->
        <div class="kpi-card kpi-rose">
          <div class="kpi-top">
            <span class="kpi-title">Nyitott Hátralék</span>
            <div class="kpi-icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
          </div>
          <div class="kpi-value text-rose">{{ formatPrice(overallStats.totalDue) }}</div>
          <div class="kpi-subtext">Fennálló tagdíjhátralék</div>
        </div>

        <!-- KPI 4: Room Rental Expenses -->
        <div class="kpi-card kpi-amber">
          <div class="kpi-top">
            <span class="kpi-title">Terembérleti Kiadások</span>
            <div class="kpi-icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
          </div>
          <div class="kpi-value text-amber">{{ formatPrice(overallStats.combinedExpenses) }}</div>
          <div class="kpi-subtext">Könyvelt számlás kiadások</div>
        </div>

        <!-- KPI 5: Net Profit / Balance -->
        <div class="kpi-card kpi-purple">
          <div class="kpi-top">
            <span class="kpi-title">Nettó Mérleg</span>
            <div class="kpi-icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                <polyline points="17 6 23 6 23 12"/>
              </svg>
            </div>
          </div>
          <div class="kpi-value" :class="(overallStats.netProfit || 0) >= 0 ? 'text-emerald' : 'text-rose'">
            {{ formatPrice(overallStats.netProfit) }}
          </div>
          <div class="kpi-subtext">Időszaki pénzügyi eredmény</div>
        </div>

        <!-- KPI 6: Active Dancers -->
        <div class="kpi-card kpi-cyan">
          <div class="kpi-top">
            <span class="kpi-title">Aktív Táncosok</span>
            <div class="kpi-icon-box">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
            </div>
          </div>
          <div class="kpi-value text-dark">{{ overallStats.activeChildren || (childrenStats ? childrenStats.length : 0) }} fő</div>
          <div class="kpi-subtext">Nyilvántartott létszám</div>
        </div>
      </div>

      <!-- Section 1: Groups Breakdown Panel -->
      <div class="panel-card mb-4">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <div class="panel-icon bg-blue-subtle text-blue">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <h2 class="panel-title">Csoportos Pénzügyi Bontás</h2>
              <p class="panel-subtitle">Csoportonkénti bevételi és hátralék kimutatások</p>
            </div>
            <span class="count-pill bg-blue-light text-blue-dark ms-2">
              {{ filteredGroups ? filteredGroups.length : 0 }} csoport
            </span>
          </div>

          <div class="modern-search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="search-svg">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input 
              type="text" 
              v-model="groupFilter" 
              class="search-input" 
              placeholder="Szűrés csoport neve alapján..." 
            />
          </div>
        </div>

        <div v-if="filteredGroups && filteredGroups.length > 0" class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th class="ps-4">Csoport Neve</th>
                <th>Létszám</th>
                <th>Előírt Tandíj</th>
                <th>Befizetett Feltöltések</th>
                <th class="text-end pe-4">Nyitott Hátralék</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in filteredGroups" :key="g.id">
                <td class="ps-4">
                  <div class="d-flex align-items-center gap-2">
                    <span class="group-name-text">{{ g.name }}</span>
                    <span v-if="g.def_name" class="code-badge">{{ g.def_name }}</span>
                  </div>
                </td>
                <td class="text-muted font-weight-medium">{{ g.childrenCount || 0 }} fő</td>
                <td class="font-weight-bold text-dark">{{ formatPrice(g.totalRevenue) }}</td>
                <td class="font-weight-bold text-emerald">{{ formatPrice(g.totalPaid) }}</td>
                <td class="text-end pe-4 font-weight-bold" :class="g.totalDue > 0 ? 'text-rose' : 'text-muted'">
                  {{ formatPrice(g.totalDue) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state py-4 text-center">
          {{ groupFilter ? 'Nincs a keresésnek megfelelő csoport.' : 'Nincs elérhető csoportos statisztika az adott időszakban.' }}
        </div>
      </div>

      <!-- Section 2: Teachers Breakdown Panel -->
      <div class="panel-card mb-4">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <div class="panel-icon bg-indigo-subtle text-indigo">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                <circle cx="12" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <h2 class="panel-title">Oktatók Szerinti Bontás & Összesítő</h2>
              <p class="panel-subtitle">Oktatói csoportok bevételei és beszedett díjai</p>
            </div>
            <span class="count-pill bg-indigo-light text-indigo-dark ms-2">
              {{ filteredTeachers ? filteredTeachers.length : 0 }} oktató
            </span>
          </div>

          <div class="modern-search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="search-svg">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input 
              type="text" 
              v-model="teacherFilter" 
              class="search-input" 
              placeholder="Keresés oktató neve vagy emailje..." 
            />
          </div>
        </div>

        <!-- Teacher Summary Cards Banner -->
        <div class="teacher-summary-row mb-3">
          <div class="t-summary-box">
            <span class="t-summary-label">Oktatói Csoportok Előírt Tandíja</span>
            <span class="t-summary-value text-blue">{{ formatPrice(teacherSummary.totalRevenue) }}</span>
          </div>
          <div class="t-summary-box">
            <span class="t-summary-label">Beszedett Feltöltések Összesen</span>
            <span class="t-summary-value text-emerald">{{ formatPrice(teacherSummary.totalPaid) }}</span>
          </div>
          <div class="t-summary-box">
            <span class="t-summary-label">Oktatói Csoportok Nyitott Hátraléka</span>
            <span class="t-summary-value text-rose">{{ formatPrice(teacherSummary.totalDue) }}</span>
          </div>
        </div>

        <div v-if="filteredTeachers && filteredTeachers.length > 0" class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th class="ps-4">Oktató Neve</th>
                <th>Email Cím</th>
                <th>Beosztott Csoportok</th>
                <th>Előírt Tandíj</th>
                <th>Befizetett Feltöltések</th>
                <th class="text-end pe-4">Nyitott Egyenleg</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in filteredTeachers" :key="t.id">
                <td class="ps-4">
                  <div class="d-flex align-items-center gap-3">
                    <div class="avatar-circle bg-indigo-subtle text-indigo font-weight-bold">
                      {{ getInitials(t.name) }}
                    </div>
                    <span class="font-weight-bold text-dark">{{ t.name }}</span>
                  </div>
                </td>
                <td class="text-muted small">{{ t.email || '-' }}</td>
                <td class="text-muted small font-weight-medium">{{ t.groupsCount || 0 }} csoport</td>
                <td class="font-weight-bold text-dark">{{ formatPrice(t.totalRevenue) }}</td>
                <td class="font-weight-bold text-emerald">{{ formatPrice(t.totalPaid) }}</td>
                <td class="text-end pe-4 font-weight-bold" :class="t.totalDue > 0 ? 'text-rose' : 'text-muted'">
                  {{ formatPrice(t.totalDue) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state py-4 text-center">
          {{ teacherFilter ? 'Nincs a keresésnek megfelelő oktató.' : 'Nincs elérhető oktatói statisztika az adott időszakban.' }}
        </div>
      </div>

      <!-- Section 3: Dancers Breakdown & Billing Links Panel -->
      <div class="panel-card mb-4">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <div class="panel-icon bg-emerald-subtle text-emerald">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
              </svg>
            </div>
            <div>
              <h2 class="panel-title">Egyéni Táncosi Kredit- és Egyenlegnyilvántartás</h2>
              <p class="panel-subtitle">Táncosok befizetései, számlái és számlázó oldaluk közvetlen megnyitása</p>
            </div>
            <span class="count-pill bg-emerald-light text-emerald-dark ms-2">
              {{ filteredDancers ? filteredDancers.length : 0 }} fő
            </span>
          </div>

          <div class="modern-search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="search-svg">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input 
              type="text" 
              v-model="dancerFilter" 
              class="search-input" 
              placeholder="Keresés táncos vagy szülő neve alapján..." 
            />
          </div>
        </div>

        <div v-if="filteredDancers && filteredDancers.length > 0" class="table-container scrollable-table-wrap">
          <table class="modern-table">
            <thead>
              <tr>
                <th class="ps-4">Táncos Neve</th>
                <th>Szülő / Gondviselő Email</th>
                <th>Elsődleges Csoport</th>
                <th>Befizetett (Feltöltés)</th>
                <th>Számlázott (Tandíj)</th>
                <th>Kredit Egyenleg</th>
                <th class="text-end pe-4">Billing Megnyitása</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in filteredDancers" :key="d.id">
                <td class="ps-4">
                  <div class="d-flex align-items-center gap-3">
                    <div class="avatar-circle bg-blue-subtle text-blue font-weight-bold">
                      {{ getInitials(d.name) }}
                    </div>
                    <router-link :to="'/children/' + d.id + '/billing'" class="dancer-link">
                      {{ d.name }}
                    </router-link>
                  </div>
                </td>
                <td class="text-muted small">{{ d.parentEmail || '-' }}</td>
                <td>
                  <span v-if="d.groupName" class="group-pill">{{ d.groupName }}</span>
                  <span v-else class="text-muted small">&mdash;</span>
                </td>
                <td class="font-weight-bold text-emerald">{{ formatPrice(d.totalPaid) }}</td>
                <td class="font-weight-bold text-dark">{{ formatPrice(d.totalDue) }}</td>
                <td>
                  <span class="balance-badge" :class="(d.balance || 0) >= 0 ? 'pos' : 'neg'">
                    {{ formatPrice(d.balance) }}
                  </span>
                </td>
                <td class="text-end pe-4">
                  <router-link :to="'/children/' + d.id + '/billing'" class="billing-action-btn">
                    <span>Billing</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state py-4 text-center">
          {{ dancerFilter ? 'Nincs a keresésnek megfelelő táncos.' : 'Nincs elérhető táncos statisztika.' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import toastMixin from '../mixins/toast.js';

export default {
  name: 'PaymentStatsItem',
  mixins: [toastMixin],
  data() {
    return {
      overallStats: {
        totalRevenue: 0,
        totalPaid: 0,
        totalDue: 0,
        activeChildren: 0,
        billingRevenue: 0,
        walletTopups: 0,
        receiptsTotal: 0,
        combinedRevenue: 0,
        combinedPaid: 0,
        combinedExpenses: 0,
        netProfit: 0
      },
      groupStats: [],
      teacherStats: [],
      childrenStats: [],
      groupFilter: '',
      teacherFilter: '',
      dancerFilter: '',
      dateFilter: 'month',
      customStartDate: '',
      customEndDate: '',
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      loading: false,
      dateFilterOptions: [
        { label: 'Hónap szerint', value: 'month' },
        { label: 'Összes időszak', value: 'all' },
        { label: 'Aktuális hét', value: 'week' },
        { label: 'Aktuális év', value: 'year' },
        { label: 'Egyedi időszak', value: 'custom' }
      ],
      monthNames: [
        'Január', 'Február', 'Március', 'Április', 'Május', 'Június',
        'Július', 'Augusztus', 'Szeptember', 'Október', 'November', 'December'
      ]
    };
  },
  computed: {
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = currentYear - 5; i <= currentYear + 1; i++) {
        years.push(i);
      }
      return years;
    },
    filteredGroups() {
      if (!this.groupStats) return [];
      if (!this.groupFilter.trim()) return this.groupStats;
      const q = this.groupFilter.toLowerCase().trim();
      return this.groupStats.filter(g =>
        (g.name && g.name.toLowerCase().includes(q)) ||
        (g.def_name && g.def_name.toLowerCase().includes(q))
      );
    },
    filteredTeachers() {
      if (!this.teacherStats) return [];
      if (!this.teacherFilter.trim()) return this.teacherStats;
      const q = this.teacherFilter.toLowerCase().trim();
      return this.teacherStats.filter(t =>
        (t.name && t.name.toLowerCase().includes(q)) ||
        (t.email && t.email.toLowerCase().includes(q))
      );
    },
    filteredDancers() {
      if (!this.childrenStats) return [];
      if (!this.dancerFilter.trim()) return this.childrenStats;
      const q = this.dancerFilter.toLowerCase().trim();
      return this.childrenStats.filter(d => 
        (d.name && d.name.toLowerCase().includes(q)) ||
        (d.parentEmail && d.parentEmail.toLowerCase().includes(q)) ||
        (d.groupName && d.groupName.toLowerCase().includes(q))
      );
    },
    teacherSummary() {
      if (!this.teacherStats || this.teacherStats.length === 0) {
        return { totalRevenue: 0, totalPaid: 0, totalDue: 0 };
      }
      return this.teacherStats.reduce((acc, t) => {
        acc.totalRevenue += (t.totalRevenue || 0);
        acc.totalPaid += (t.totalPaid || 0);
        acc.totalDue += (t.totalDue || 0);
        return acc;
      }, { totalRevenue: 0, totalPaid: 0, totalDue: 0 });
    }
  },
  mounted() {
    this.loadStats();
  },
  methods: {
    setDateFilter(val) {
      this.dateFilter = val;
      this.onDateFilterChange();
    },
    async loadStats() {
      this.loading = true;
      try {
        const dateParams = this.getDateParameters();
        const [overallRes, groupsRes, teachersRes, childrenRes] = await Promise.all([
          axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/overall', { params: dateParams }),
          axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/groups', { params: dateParams }),
          axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/teachers', { params: dateParams }),
          axios.get(this.$BACKEND_HOST + '/api/pricing/statistics/children', { params: dateParams })
        ]);

        this.overallStats = overallRes.data.stats || {};
        this.groupStats = groupsRes.data.stats || [];
        this.teacherStats = teachersRes.data.stats || [];
        this.childrenStats = childrenRes.data.children || childrenRes.data.stats || (Array.isArray(childrenRes.data) ? childrenRes.data : []);
      } catch (error) {
        console.error('Error loading payment stats:', error);
        this.showToast('Hiba a statisztikák betöltése során', 'error');
      } finally {
        this.loading = false;
      }
    },
    getDateParameters() {
      const params = {};
      
      if (this.dateFilter === 'week') {
        const now = new Date();
        const startOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 1));
        const endOfWeek = new Date(now.setDate(now.getDate() - now.getDay() + 7));
        params.start_date = startOfWeek.toISOString().split('T')[0];
        params.end_date = endOfWeek.toISOString().split('T')[0];
      } else if (this.dateFilter === 'month') {
        const startOfMonth = new Date(this.selectedYear, this.selectedMonth - 1, 1);
        const endOfMonth = new Date(this.selectedYear, this.selectedMonth, 0);
        params.start_date = startOfMonth.toISOString().split('T')[0];
        params.end_date = endOfMonth.toISOString().split('T')[0];
      } else if (this.dateFilter === 'year') {
        const startOfYear = new Date(this.selectedYear, 0, 1);
        const endOfYear = new Date(this.selectedYear, 11, 31);
        params.start_date = startOfYear.toISOString().split('T')[0];
        params.end_date = endOfYear.toISOString().split('T')[0];
      } else if (this.dateFilter === 'custom') {
        if (this.customStartDate) {
          params.start_date = this.customStartDate;
        }
        if (this.customEndDate) {
          params.end_date = this.customEndDate;
        }
      }
      
      return params;
    },
    onDateFilterChange() {
      if (this.dateFilter !== 'custom') {
        this.customStartDate = '';
        this.customEndDate = '';
      }
      this.loadStats();
    },
    formatPrice(val) {
      if (!val) return '0 Ft';
      return Math.round(val).toLocaleString('hu-HU') + ' Ft';
    },
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
    downloadXLSX() {
      const rows = [
        ['MUTATÓ', 'ÉRTÉK'],
        ['Összes számlázott tandíj', this.overallStats.combinedRevenue || 0],
        ['Befizetett feltöltések', this.overallStats.combinedPaid || 0],
        ['Kiadott számlák', this.overallStats.totalDue || 0],
        ['Terembérlet kiadások', this.overallStats.combinedExpenses || 0],
        ['Nettó egyenleg', this.overallStats.netProfit || 0],
        ['Aktív táncosok száma', this.overallStats.activeChildren || (this.childrenStats ? this.childrenStats.length : 0)],
        [],
        ['CSOPORT', 'LÉTSZÁM', 'SZÁMLÁZOTT TANDÍJ', 'BEFIZETETT FELTÖLTÉSEK', 'TEREM KIADÁSOK'],
        ...(this.groupStats || []).map(g => [g.name, g.childrenCount, g.totalRevenue, g.totalPaid, g.totalDue]),
        [],
        ['OKTATÓ', 'EMAIL', 'SZÁMLÁZOTT TANDÍJ', 'BEFIZETETT FELTÖLTÉSEK', 'NYITOTT EGYENLEG'],
        ...(this.teacherStats || []).map(t => [t.name, t.email, t.totalRevenue, t.totalPaid, t.totalDue]),
        [],
        ['TÁNCOS', 'SZÜLŐ EMAIL', 'CSOPORT', 'BEFIZETETT FELTÖLTÉS', 'SZÁMLÁZOTT TANDÍJ', 'KREDIT EGYENLEG'],
        ...(this.childrenStats || []).map(d => [d.name, d.parentEmail, d.groupName || '-', d.totalPaid, d.totalDue, d.balance])
      ];

      const csvContent = 'data:text/csv;charset=utf-8,\uFEFF' + rows.map(e => e.join(';')).join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `fizetesi_statisztika_osszesito_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
};
</script>

<style scoped>
/* Main Dashboard Canvas Layout */
.stats-dashboard {
  max-width: 1380px;
  margin: 0 auto;
  padding: 1.75rem 1.25rem 3rem 1.25rem;
  font-family: inherit;
}

/* Hero Header Banner */
.stats-hero {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #0ea5e9 100%);
  border-radius: 20px;
  padding: 1.75rem 2rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  box-shadow: 0 10px 25px -5px rgba(37, 99, 235, 0.35);
  margin-bottom: 1.5rem;
  position: relative;
  overflow: hidden;
}
.stats-hero::after {
  content: '';
  position: absolute;
  top: -50%;
  right: -10%;
  width: 320px;
  height: 320px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.25) 0%, rgba(255,255,255,0) 70%);
  pointer-events: none;
}

.hero-content {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  z-index: 1;
}

.hero-icon-badge {
  width: 54px;
  height: 54px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  flex-shrink: 0;
}

.hero-title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0 0 0.25rem 0;
  color: #ffffff;
  letter-spacing: -0.3px;
}

.hero-subtitle {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.4;
}

.export-hero-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  border: none;
  border-radius: 30px;
  padding: 0.65rem 1.4rem;
  font-size: 0.875rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(16, 185, 129, 0.35);
  transition: all 0.2s ease;
  z-index: 1;
  white-space: nowrap;
}
.export-hero-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.45);
}

/* Control Panel Card */
.control-panel-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 16px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  margin-bottom: 1.75rem;
}

.control-left {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.control-label {
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-text, #0f172a);
  display: flex;
  align-items: center;
}

.filter-pills {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: #f1f5f9;
  padding: 4px;
  border-radius: 30px;
}

.filter-pill {
  background: transparent;
  border: none;
  border-radius: 20px;
  padding: 0.4rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
}
.filter-pill:hover {
  color: #0f172a;
}
.filter-pill.active {
  background: #ffffff;
  color: #2563eb;
  font-weight: 700;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.modern-select {
  border-radius: 12px;
  border: 1px solid var(--color-border, #cbd5e1);
  background-color: var(--card-bg, #ffffff);
  color: var(--color-text, #0f172a);
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.45rem 1rem;
  outline: none;
  transition: all 0.2s ease;
}
.modern-select:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.month-selector-group, .date-range-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modern-date-input {
  border-radius: 12px;
  border: 1px solid var(--color-border, #cbd5e1);
  background-color: var(--card-bg, #ffffff);
  color: var(--color-text, #0f172a);
  font-size: 0.85rem;
  padding: 0.4rem 0.85rem;
}

.range-separator {
  color: #94a3b8;
  font-weight: 700;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.loading-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #64748b;
  margin-top: 1rem;
}

/* Top 6 KPI Dashboard Cards Grid */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
}

.kpi-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 16px;
  padding: 1.1rem 1.25rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.02);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  position: relative;
  overflow: hidden;
}
.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px -5px rgba(0, 0, 0, 0.07);
}

.kpi-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.65rem;
}

.kpi-title {
  font-size: 0.73rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
}

.kpi-icon-box {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-blue .kpi-icon-box { background: #eff6ff; color: #2563eb; }
.kpi-emerald .kpi-icon-box { background: #ecfdf5; color: #10b981; }
.kpi-rose .kpi-icon-box { background: #fff1f2; color: #f43f5e; }
.kpi-amber .kpi-icon-box { background: #fffbeb; color: #f59e0b; }
.kpi-purple .kpi-icon-box { background: #f5f3ff; color: #8b5cf6; }
.kpi-cyan .kpi-icon-box { background: #ecfeff; color: #06b6d4; }

.kpi-value {
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
  margin-bottom: 0.35rem;
  letter-spacing: -0.3px;
}

.kpi-subtext {
  font-size: 0.75rem;
  color: #94a3b8;
}

.text-emerald { color: #10b981 !important; }
.text-rose { color: #f43f5e !important; }
.text-amber { color: #f59e0b !important; }
.text-blue { color: #2563eb !important; }

/* Panel Cards Section */
.panel-card {
  background: var(--card-bg, #ffffff);
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 18px;
  padding: 1.5rem;
  box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.03);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
}

.panel-title-wrap {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.panel-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.bg-blue-subtle { background: #eff6ff; }
.bg-indigo-subtle { background: #e0e7ff; }
.bg-emerald-subtle { background: #ecfdf5; }

.text-indigo { color: #4f46e5 !important; }

.panel-title {
  font-size: 1.05rem;
  font-weight: 800;
  color: var(--color-heading, #0f172a);
  margin: 0 0 0.15rem 0;
  letter-spacing: -0.2px;
}

.panel-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
}

.count-pill {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.bg-blue-light { background: #dbeafe; }
.text-blue-dark { color: #1e40af; }
.bg-indigo-light { background: #e0e7ff; }
.text-indigo-dark { color: #3730a3; }
.bg-emerald-light { background: #d1fae5; }
.text-emerald-dark { color: #065f46; }

/* Modern Search Box */
.modern-search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-svg {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  pointer-events: none;
  z-index: 2;
}

.search-input {
  width: 280px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid var(--color-border, #cbd5e1);
  background-color: var(--card-bg, #ffffff);
  color: var(--color-text, #0f172a);
  font-size: 0.85rem;
  padding-left: 2.75rem !important;
  padding-right: 1rem;
  outline: none;
  transition: all 0.2s ease;
}
.search-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.14);
}

/* Teacher Summary Row Banner */
.teacher-summary-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.85rem;
}

.t-summary-box {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 0.85rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.t-summary-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}

.t-summary-value {
  font-size: 1.1rem;
  font-weight: 800;
}

/* Table Container & Modern Table Styling */
.table-container {
  border: 1px solid var(--color-border, #e2e8f0);
  border-radius: 14px;
  overflow: hidden;
  margin-top: 1rem;
}

.scrollable-table-wrap {
  max-height: 520px;
  overflow-y: auto;
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.modern-table thead th {
  background: #f8fafc;
  font-size: 0.73rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  padding: 0.9rem 1.15rem;
  border-bottom: 2px solid #e2e8f0;
}

.modern-table tbody tr {
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.15s ease;
}
.modern-table tbody tr:last-child {
  border-bottom: none;
}
.modern-table tbody tr:hover {
  background-color: rgba(37, 99, 235, 0.035);
}

.modern-table tbody td {
  padding: 0.9rem 1.15rem;
  font-size: 0.88rem;
  vertical-align: middle;
  color: var(--color-text, #0f172a);
}

.group-name-text {
  font-weight: 700;
  color: #0f172a;
}

.code-badge {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 700;
}

.avatar-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  flex-shrink: 0;
}

.dancer-link {
  font-weight: 700;
  color: #0f172a;
  text-decoration: none;
  transition: color 0.15s ease;
}
.dancer-link:hover {
  color: #2563eb;
  text-decoration: underline;
}

.group-pill {
  background: #f1f5f9;
  color: #334155;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
}

.balance-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 800;
}
.balance-badge.pos {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
}
.balance-badge.neg {
  background: #fff1f2;
  color: #e11d48;
  border: 1px solid #fecdd3;
}

.billing-action-btn {
  background: #eff6ff;
  color: #2563eb;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  padding: 0.4rem 0.95rem;
  font-size: 0.78rem;
  font-weight: 700;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.2s ease;
}
.billing-action-btn:hover {
  background: #2563eb;
  color: #ffffff;
  border-color: #2563eb;
  transform: translateX(2px);
}

.empty-state {
  color: #94a3b8;
  font-size: 0.85rem;
  font-weight: 500;
}
</style>
